import BaseAccordion from 'components/Accordions/BaseAccordion';
import { MODE_BUTTON_ACTIVE_COLOR } from 'components/Buttons/ModeButton';
import BaseTable from 'components/Tables/BaseTable';
import TextToggle from 'components/UI/TextToggle';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import { getAIAnalytics } from 'mocks/aiAnalytics.mock';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Container, Form } from 'react-bootstrap';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { alertColorScale, getSeverityLevel } from 'utils/alertUtils';

const STATUS_OPTIONS = ['Close', 'Open'];
const COLUMN_SIZES = {
  alertId: 90,
  severity: 160,
  score: 110,
  type: 190,
  location: 150,
  time: 120,
  date: 120,
  status: 300,
};

const filterInputSx = {
  backgroundColor: 'var(--color-primary-shadow)',
  borderRadius: '999px',
  color: 'var(--bs-body-color)',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--color-primary)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--color-primary-highlight)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--color-primary-highlight)',
  },
  input: {
    padding: '6px 12px',
  },
  '& .MuiSvgIcon-root': {
    color: 'var(--color-secondary)',
  },
};

export default function AIAnalytics() {
  const navigate = useNavigate();
  const [fetching, setFetching] = useState(true);
  const [analytics, setAnalytics] = useState([]);
  const [mobileSortField, setMobileSortField] = useState('alertId');
  const [mobileSortDirection, setMobileSortDirection] = useState('asc');

  useEffect(() => {
    (async () => {
      getAIAnalytics().then((res) => {
        const normalized = (res.data.result || []).map((item) => ({
          ...item,
          status:
            typeof item.status === 'string'
              ? item.status.charAt(0).toUpperCase() + item.status.slice(1).toLowerCase()
              : item.status
                ? 'Close'
                : 'Open',
        }));
        setAnalytics(normalized);
        setFetching(false);
      });
    })();
  }, []);

  const formatTime = (timestamp) =>
    new Date(timestamp).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });

  const formatDate = (timestamp) =>
    new Date(timestamp).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });

  const severityRank = useMemo(
    () => ({
      Extreme: 3,
      Severe: 2,
      Moderate: 1,
      Minor: 0,
    }),
    [],
  );

  const getSortValue = useCallback(
    (item, field) => {
      switch (field) {
        case 'severityLabel': {
          const value = getSeverityLevel(item.severity);
          return severityRank[value] ?? -1;
        }
        case 'score':
          return item.score ?? 0;
        case 'type':
          return item.type ?? '';
        case 'location':
          return item.location ?? '';
        case 'status':
          return item.status ?? '';
        case 'timeDisplay': {
          if (!item.timestamp) return 0;
          const date = new Date(item.timestamp);
          return date.getHours() * 60 + date.getMinutes();
        }
        case 'dateDisplay': {
          if (!item.timestamp) return 0;
          const date = new Date(item.timestamp);
          date.setHours(0, 0, 0, 0);
          return date.getTime();
        }
        case 'alertId':
        default:
          return item.alertId ?? '';
      }
    },
    [severityRank],
  );

  const filteredAnalytics = useMemo(() => {
    const sorted = [...analytics];
    const direction = mobileSortDirection === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      const aVal = getSortValue(a, mobileSortField);
      const bVal = getSortValue(b, mobileSortField);
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * direction;
      }
      const aStr = aVal?.toString().toLowerCase() ?? '';
      const bStr = bVal?.toString().toLowerCase() ?? '';
      if (aStr < bStr) return -direction;
      if (aStr > bStr) return direction;
      return 0;
    });
    return sorted;
  }, [analytics, getSortValue, mobileSortDirection, mobileSortField]);

  const tableRows = useMemo(
    () =>
      filteredAnalytics.map((item) => ({
        ...item,
        id: item.alertId,
        timeDisplay: formatTime(item.timestamp),
        dateDisplay: formatDate(item.timestamp),
        severityLabel: getSeverityLevel(item.severity),
      })),
    [filteredAnalytics],
  );

  const severityOptions = useMemo(
    () => Array.from(new Set(analytics.map((item) => getSeverityLevel(item.severity)))).sort(),
    [analytics],
  );

  const statusOptions = useMemo(
    () => Array.from(new Set(analytics.map((item) => item.status))).sort(),
    [analytics],
  );

  const mobileSortOptions = useMemo(
    () => [
      { label: 'Alert Id', value: 'alertId' },
      { label: 'Severity', value: 'severityLabel' },
      { label: 'Score', value: 'score' },
      { label: 'Type', value: 'type' },
      { label: 'Location', value: 'location' },
      { label: 'Time', value: 'timeDisplay' },
      { label: 'Date', value: 'dateDisplay' },
      { label: 'Status', value: 'status' },
    ],
    [],
  );

  const handleStatusChange = useCallback((alertId, value) => {
    setAnalytics((prev) =>
      prev.map((instance) =>
        instance.alertId === alertId ? { ...instance, status: value } : instance,
      ),
    );
  }, []);

  const renderStatusActions = useCallback(
    (alertId, status = STATUS_OPTIONS[0], fsButton = false) => {
      const isOpen = status?.toLowerCase() === 'open';
      return (
        <div
          className={`status-actions d-flex ${
            fsButton ? 'flex-column' : 'flex-column flex-lg-row'
          } align-items-stretch align-items-lg-center justify-content-end gap-3 flex-wrap`}
        >
          <div className="flex-grow-1" style={{ minWidth: fsButton ? 'auto' : 180 }}>
            <TextToggle
              options={STATUS_OPTIONS}
              value={isOpen}
              setValue={(value) =>
                handleStatusChange(alertId, value ? STATUS_OPTIONS[1] : STATUS_OPTIONS[0])
              }
              wrapperClassName={fsButton ? 'mode-button-mobile' : ''}
            />
          </div>
          <Button
            variant={fsButton ? 'primary' : 'link'}
            className={`fw-semibold ${
              fsButton ? 'w-100 text-white' : 'text-white text-decoration-none p-0'
            }`}
            style={
              fsButton
                ? {
                    backgroundColor: MODE_BUTTON_ACTIVE_COLOR,
                    borderColor: MODE_BUTTON_ACTIVE_COLOR,
                    borderRadius: 12,
                    paddingTop: '10px',
                    paddingBottom: '10px',
                  }
                : { whiteSpace: 'normal' }
            }
            onClick={() => navigate(`/publicuser/createalert`)}
          >
            Create Alert
          </Button>
        </div>
      );
    },
    [handleStatusChange, navigate],
  );

  const columns = useMemo(
    () => [
      {
        header: 'Alert Id',
        accessorKey: 'alertId',
        enableSorting: true,
        size: COLUMN_SIZES.alertId,
        minSize: COLUMN_SIZES.alertId,
        maxSize: COLUMN_SIZES.alertId,
        enableColumnActions: false,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: 'Filter alert id',
          variant: 'outlined',
          sx: filterInputSx,
        },
        Cell: ({ cell }) => <span className="text-white">{cell.getValue()}</span>,
      },
      {
        header: 'Severity',
        accessorKey: 'severityLabel',
        enableSorting: true,
        size: COLUMN_SIZES.severity,
        minSize: COLUMN_SIZES.severity,
        maxSize: COLUMN_SIZES.severity,
        enableColumnActions: false,
        enableColumnFilter: true,
        filterVariant: 'select',
        filterSelectOptions: severityOptions,
        muiFilterTextFieldProps: {
          variant: 'outlined',
          sx: filterInputSx,
          placeholder: 'Severity',
        },
        muiFilterSelectProps: {
          displayEmpty: true,
          sx: filterInputSx,
        },
        sortingFn: (rowA, rowB, columnId) => {
          const aVal = severityRank[rowA.getValue(columnId)] ?? -1;
          const bVal = severityRank[rowB.getValue(columnId)] ?? -1;
          return aVal - bVal;
        },
        Cell: ({ cell, row }) => {
          const severity = row.original.severity;
          return (
            <div className="d-flex align-items-center gap-2 text-white">
              <div
                style={{
                  borderRadius: '50%',
                  height: 20,
                  width: 20,
                  border: '1px solid var(--bs-body-color)',
                  background: alertColorScale(severity),
                }}
              />
              {cell.getValue()}
            </div>
          );
        },
      },
      {
        header: 'Score',
        accessorKey: 'score',
        enableSorting: true,
        size: COLUMN_SIZES.score,
        minSize: COLUMN_SIZES.score,
        maxSize: COLUMN_SIZES.score,
        enableColumnActions: false,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: 'Filter score',
          variant: 'outlined',
          sx: filterInputSx,
        },
        Cell: ({ cell, row }) => {
          const score = cell.getValue();
          const scoreDiff = row.original.scoreDiff;
          return (
            <div className="d-flex align-items-center justify-content-between gap-2 text-white">
              <span>{score}%</span>
              {scoreDiff === 0 ? null : scoreDiff > 0 ? <FaArrowUp /> : <FaArrowDown />}
            </div>
          );
        },
      },
      {
        header: 'Type',
        accessorKey: 'type',
        enableSorting: true,
        size: COLUMN_SIZES.type,
        minSize: COLUMN_SIZES.type,
        maxSize: COLUMN_SIZES.type,
        enableColumnActions: false,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: 'Filter type',
          variant: 'outlined',
          sx: filterInputSx,
        },
        Cell: ({ cell }) => <span className="text-white">{cell.getValue()}</span>,
      },
      {
        header: 'Location',
        accessorKey: 'location',
        enableSorting: true,
        size: COLUMN_SIZES.location,
        minSize: COLUMN_SIZES.location,
        maxSize: COLUMN_SIZES.location,
        enableColumnActions: false,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: 'Filter location',
          variant: 'outlined',
          sx: filterInputSx,
        },
        Cell: ({ cell }) => <span className="text-white">{cell.getValue()}</span>,
      },
      {
        header: 'Time',
        accessorKey: 'timeDisplay',
        enableSorting: true,
        size: COLUMN_SIZES.time,
        minSize: COLUMN_SIZES.time,
        maxSize: COLUMN_SIZES.time,
        enableColumnActions: false,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: 'Filter time',
          variant: 'outlined',
          sx: filterInputSx,
        },
        sortingFn: (rowA, rowB) => {
          const aTime = rowA.original.timestamp ? new Date(rowA.original.timestamp).getTime() : 0;
          const bTime = rowB.original.timestamp ? new Date(rowB.original.timestamp).getTime() : 0;
          return aTime - bTime;
        },
        Cell: ({ cell }) => <span className="text-white">{cell.getValue()}</span>,
      },
      {
        header: 'Date',
        accessorKey: 'dateDisplay',
        enableSorting: true,
        size: COLUMN_SIZES.date,
        minSize: COLUMN_SIZES.date,
        maxSize: COLUMN_SIZES.date,
        muiTableHeadCellProps: { align: 'left' },
        muiTableBodyCellProps: { align: 'left' },
        enableColumnActions: false,
        enableColumnFilter: true,
        filterVariant: 'text',
        muiFilterTextFieldProps: {
          type: 'date',
          variant: 'outlined',
          sx: filterInputSx,
          InputLabelProps: { shrink: false },
        },
        sortingFn: (rowA, rowB) => {
          const getDay = (row) => {
            if (!row.original.timestamp) return 0;
            const d = new Date(row.original.timestamp);
            d.setHours(0, 0, 0, 0);
            return d.getTime();
          };
          return getDay(rowA) - getDay(rowB);
        },
        Cell: ({ cell }) => (
          <span className="text-white d-block text-center">{cell.getValue()}</span>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        enableSorting: true,
        size: COLUMN_SIZES.status,
        minSize: COLUMN_SIZES.status,
        grow: true,
        enableColumnActions: false,
        enableColumnFilter: true,
        filterVariant: 'select',
        filterSelectOptions: statusOptions,
        muiFilterTextFieldProps: {
          variant: 'outlined',
          sx: filterInputSx,
        },
        muiFilterSelectProps: {
          displayEmpty: true,
          sx: filterInputSx,
        },
        Cell: ({ row }) => {
          const { alertId, status } = row.original;
          return renderStatusActions(alertId, status);
        },
      },
    ],
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [renderStatusActions, severityOptions, severityRank, statusOptions],
  );

  if (fetching) {
    return <WidgetLoadingAnimation />;
  }

  if (
    // window.origin.toLowerCase().includes("localhost") ||
    window.origin.toLowerCase().includes('comal') ||
    window.origin.toLowerCase().includes('pittsburgh') ||
    window.origin.toLowerCase().includes('harrison') ||
    window.origin.toLowerCase().includes('gillespie')
  ) {
    return 'This feature is still being optimized for your organization and will be available soon. Please contact your Administrator for any further details or updates.';
  }

  return (
    <Container fluid className="d-flex flex-column h-100 overflow-hidden py-3 px-0 px-lg-3">
      <Card
        className="flex-grow-1 border-0 shadow-sm d-flex flex-column min-h-0"
        style={{ backgroundColor: '#222a2d', overflow: 'hidden' }}
      >
        <Card.Body className="d-flex flex-column gap-4 flex-grow-1 min-h-0">
          <div className="flex-shrink-0">
            <div className="fw-bold fs-3 text-white">Instances</div>
          </div>
          <div className="flex-grow-1 overflow-hidden d-flex flex-column min-h-0">
            <div className="d-none d-lg-flex flex-column flex-grow-1 ai-analytics-table px-0 min-h-0">
              <BaseTable
                rows={tableRows}
                columns={columns}
                altRowSettings={{
                  alternate: true,
                  color1: '#222a2d',
                  color2: '#2c373b',
                }}
                muiTablePaperProps={{
                  sx: { height: '100%', display: 'flex', flexDirection: 'column' },
                }}
                muiTableContainerProps={{ sx: { flex: 1, overflowY: 'auto' } }}
                customStyles={{
                  container: {
                    borderRadius: 0,
                    overflow: 'hidden',
                    backgroundColor: '#2c373b',
                    border: 'none',
                  },
                  header: {
                    backgroundColor: '#222a2d',
                    fontWeight: 600,
                    color: '#ffffff',
                    '& .MuiTableSortLabel-root': {
                      color: '#ffffff !important',
                    },
                    '& .MuiTableSortLabel-root.Mui-active': {
                      color: '#ffffff !important',
                    },
                    '& .MuiTableSortLabel-root:hover': {
                      color: '#ffffff !important',
                    },
                    '& .MuiTableSortLabel-icon': {
                      color: '#ffffff !important',
                      fill: '#ffffff !important',
                    },
                  },
                  cell: {
                    borderBottom: '1px solid #56577a',
                    color: '#ffffff',
                    fontSize: '0.95rem',
                    backgroundColor: 'transparent',
                  },
                }}
              />
            </div>

            <div className="d-block d-lg-none flex-grow-1 overflow-auto ai-analytics-mobile-accordion">
              <div className="mb-3 d-flex flex-column flex-sm-row gap-2">
                <Form.Select
                  className="text-white border-0"
                  style={{
                    backgroundColor: '#2c373b',
                    borderRadius: '999px',
                  }}
                  value={mobileSortField}
                  onChange={(e) => setMobileSortField(e.target.value)}
                >
                  {mobileSortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      Sort by {option.label}
                    </option>
                  ))}
                </Form.Select>
                <Button
                  style={{
                    backgroundColor: '#2c373b',
                    borderColor: '#2c373b',
                    borderRadius: '999px',
                  }}
                  className="text-white"
                  onClick={() =>
                    setMobileSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
                  }
                >
                  {mobileSortDirection === 'asc' ? 'Ascending' : 'Descending'}
                </Button>
              </div>
              {filteredAnalytics.map((item, index) => (
                <div key={`${item.alertId}-${index}`} className="mb-3">
                  <BaseAccordion
                    eventKey={`${item.alertId}-${index}`}
                    bodyBackground="#2c373b"
                    header={
                      <div className="w-100 d-flex flex-column">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-semibold">{item.type || item.alertId}</span>
                          <span className="text-capitalize">{item.status}</span>
                        </div>
                        <small>
                          {formatDate(item.timestamp)} • {formatTime(item.timestamp)}
                        </small>
                      </div>
                    }
                  >
                    <div className="d-flex flex-column gap-2 text-white">
                      <div className="d-flex align-items-center gap-2">
                        <div
                          style={{
                            borderRadius: '50%',
                            height: 20,
                            width: 20,
                            border: '1px solid var(--bs-body-color)',
                            background: alertColorScale(item.severity),
                          }}
                        />
                        <span>{getSeverityLevel(item.severity)}</span>
                      </div>
                      <div className="d-flex justify-content-start align-items-center gap-2">
                        <span className="fw-semibold">Score:</span>
                        <span>{item.score}%</span>
                        {item.scoreDiff !== 0 &&
                          (item.scoreDiff > 0 ? <FaArrowUp /> : <FaArrowDown />)}
                      </div>
                      <div>
                        <span className="fw-semibold">Type:</span> {item.type}
                      </div>
                      <div>
                        <span className="fw-semibold">Location:</span> {item.location}
                      </div>
                      <div>
                        <span className="fw-semibold">Time:</span> {formatTime(item.timestamp)}
                      </div>
                      <div>
                        <span className="fw-semibold">Date:</span> {formatDate(item.timestamp)}
                      </div>
                      {renderStatusActions(item.alertId, item.status, true)}
                    </div>
                  </BaseAccordion>
                </div>
              ))}

              {filteredAnalytics.length === 0 && (
                <div className="text-center text-white-50 py-5">
                  No instances match your search.
                </div>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
}
